import React from "react";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../ui/command";
import { Users, Loader2, Trash2, UserX, UserCheck, AlertTriangle, ChevronsUpDown, Check } from "lucide-react";

const EditUserDialog = React.memo(function EditUserDialog({
  open, onOpenChange, selectedUser, selectedRole, setSelectedRole,
  selectedReportsTo, setSelectedReportsTo, reportsToSearchOpen, setReportsToSearchOpen,
  users, selectedTeamIds, setSelectedTeamIds, teamRoles, handleTeamToggle,
  handleTeamRoleChange, selectedExempt, setSelectedExempt, selectedFormId,
  setSelectedFormId, teams, forms, saving, onSave, deactivating, onDeactivate,
  onReactivate, deleteDialogOpen, setDeleteDialogOpen, userToDelete, deleting,
  onDelete, onOpenDelete
}) {
  return (
    <>
      {/* Edit User Dialog */}
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-indigo-600" />
              Edit User
            </DialogTitle>
            {/* User Name Header */}
            <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3 mt-2">
              <p className="text-lg font-semibold text-indigo-900">{selectedUser?.full_name}</p>
              <p className="text-sm text-indigo-600">{selectedUser?.email}</p>
            </div>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Default Role</Label>
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger data-testid="user-role-select">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USER">User</SelectItem>
                  <SelectItem value="TEAM_LEADER">Team Leader</SelectItem>
                  <SelectItem value="ADMIN">Admin</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                This is the user&apos;s default role. You can override it per team below.
              </p>
            </div>

            {/* Report To - Multi-select */}
            <div className="space-y-2">
              <Label>Reports To</Label>
              <Popover open={reportsToSearchOpen} onOpenChange={setReportsToSearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={reportsToSearchOpen}
                    className="w-full justify-between text-left h-auto min-h-10"
                  >
                    <span className="truncate">
                      {selectedReportsTo.length > 0
                        ? selectedReportsTo.map(id => users.find(u => u.id === id)?.full_name || id).join(", ")
                        : "Select manager(s)..."}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search users..." />
                    <CommandList>
                      <CommandEmpty>No user found.</CommandEmpty>
                      <CommandGroup>
                        {selectedReportsTo.length > 0 && (
                          <CommandItem
                            value="__clear_all__"
                            onSelect={() => setSelectedReportsTo([])}
                          >
                            <Check className="mr-2 h-4 w-4 opacity-0" />
                            <span className="text-slate-400 italic">Clear all</span>
                          </CommandItem>
                        )}
                        {users.filter(u => u.id !== selectedUser?.id).map((user) => {
                          const isSelected = selectedReportsTo.includes(user.id);
                          return (
                            <CommandItem
                              key={user.id}
                              value={user.full_name}
                              onSelect={() => {
                                setSelectedReportsTo(prev =>
                                  isSelected
                                    ? prev.filter(id => id !== user.id)
                                    : [...prev, user.id]
                                );
                              }}
                            >
                              <Check className={`mr-2 h-4 w-4 ${isSelected ? "opacity-100" : "opacity-0"}`} />
                              {user.full_name}
                              <span className="ml-2 text-xs text-slate-500">({user.email})</span>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {selectedReportsTo.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {selectedReportsTo.map(id => {
                    const mgr = users.find(u => u.id === id);
                    return mgr ? (
                      <Badge key={id} variant="secondary" className="text-xs bg-indigo-50 text-indigo-700">
                        {mgr.full_name}
                      </Badge>
                    ) : null;
                  })}
                </div>
              )}
              <p className="text-xs text-slate-500">
                Select who this user reports to (can select multiple).
                Selected manager(s) will receive this user&apos;s daily log submissions and be CC&apos;d on reminders when enabled in Settings.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Teams & Roles</Label>
              <p className="text-xs text-slate-500 mb-2">
                Select teams and assign a role for each team.
              </p>
              <div className="border rounded-md p-3 space-y-3 max-h-56 overflow-y-auto">
                {teams.length === 0 ? (
                  <p className="text-sm text-slate-400">No teams available. Create teams first.</p>
                ) : (
                  teams.map((team) => {
                    const isSelected = selectedTeamIds.includes(team.id);
                    return (
                      <div key={team.id} className={`flex items-center justify-between p-2 rounded-lg ${isSelected ? 'bg-indigo-50 border border-indigo-200' : 'hover:bg-slate-50'}`}>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id={`team-${team.id}`}
                            checked={isSelected}
                            onCheckedChange={() => handleTeamToggle(team.id)}
                            data-testid={`team-checkbox-${team.id}`}
                          />
                          <label
                            htmlFor={`team-${team.id}`}
                            className="text-sm font-medium leading-none cursor-pointer"
                          >
                            {team.name}
                          </label>
                        </div>
                        {isSelected && (
                          <Select
                            value={teamRoles[team.id] || 'USER'}
                            onValueChange={(val) => handleTeamRoleChange(team.id, val)}
                          >
                            <SelectTrigger className="w-32 h-8 text-xs" data-testid={`team-role-${team.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="USER">User</SelectItem>
                              <SelectItem value="TEAM_LEADER">Team Leader</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
              {selectedTeamIds.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {selectedTeamIds.map((teamId) => {
                    const team = teams.find(t => t.id === teamId);
                    const role = teamRoles[teamId] || 'USER';
                    return team ? (
                      <Badge key={teamId} variant="secondary" className={`text-xs ${role === 'TEAM_LEADER' ? 'bg-purple-100 text-purple-700' : 'bg-slate-100'}`}>
                        {team.name} ({role === 'TEAM_LEADER' ? 'Leader' : 'User'})
                      </Badge>
                    ) : null;
                  })}
                </div>
              )}
            </div>

            {/* Exempt Checkbox */}
            <div className="flex items-center space-x-2 p-3 bg-amber-50 border border-amber-100 rounded-lg">
              <Checkbox
                id="isExempt"
                checked={selectedExempt}
                onCheckedChange={setSelectedExempt}
                data-testid="exempt-checkbox"
              />
              <div className="flex-1">
                <label
                  htmlFor="isExempt"
                  className="text-sm font-medium leading-none cursor-pointer"
                >
                  Exempt from Daily Logs
                </label>
                <p className="text-xs text-slate-500 mt-1">
                  This user won&apos;t be required to submit daily logs and won&apos;t receive reminders
                </p>
              </div>
            </div>

            {/* Form Assignment */}
            <div className="space-y-2">
              <Label>Assigned Form</Label>
              <Select value={selectedFormId || "default"} onValueChange={(val) => setSelectedFormId(val === "default" ? "" : val)}>
                <SelectTrigger data-testid="user-form-select">
                  <SelectValue placeholder="Default Form" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Default Form</SelectItem>
                  {forms.map((form) => (
                    <SelectItem key={form.id} value={form.id}>
                      {form.name} {form.is_default && "(Default)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                Select which daily log form this user should fill out
              </p>
            </div>

            {/* Danger Zone - Deactivate and Delete */}
            <div className="border-t pt-4 mt-4">
              <p className="text-sm font-medium text-slate-700 mb-3">Account Actions</p>
              <div className="space-y-2">
                {/* Deactivate/Reactivate Button */}
                {selectedUser?.is_active !== false ? (
                  <Button
                    variant="outline"
                    className="w-full justify-start text-amber-600 border-amber-200 hover:bg-amber-50 hover:text-amber-700"
                    onClick={onDeactivate}
                    disabled={deactivating || selectedUser?.role === 'ADMIN'}
                    data-testid="deactivate-user-btn"
                  >
                    {deactivating ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <UserX className="mr-2 h-4 w-4" />
                    )}
                    Deactivate User
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    className="w-full justify-start text-green-600 border-green-200 hover:bg-green-50 hover:text-green-700"
                    onClick={onReactivate}
                    disabled={deactivating}
                    data-testid="reactivate-user-btn"
                  >
                    {deactivating ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <UserCheck className="mr-2 h-4 w-4" />
                    )}
                    Reactivate User
                  </Button>
                )}
                <p className="text-xs text-slate-500 ml-1">
                  {selectedUser?.is_active !== false
                    ? "Deactivating will disable login but preserve data for reports."
                    : "This user is currently deactivated and cannot log in."}
                </p>

                {/* Delete Button */}
                <Button
                  variant="outline"
                  className="w-full justify-start text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                  onClick={onOpenDelete}
                  disabled={selectedUser?.role === 'ADMIN'}
                  data-testid="delete-user-btn"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete User
                </Button>
                <p className="text-xs text-slate-500 ml-1">
                  Permanently delete this user and all their data. This cannot be undone.
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={onSave} disabled={saving} data-testid="save-user-changes-button">
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete User Permanently?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                Are you sure you want to delete <strong>{userToDelete?.full_name}</strong>?
              </p>
              <p className="text-red-600 font-medium">
                This will permanently delete:
              </p>
              <ul className="list-disc list-inside text-sm space-y-1 ml-2">
                <li>User account and profile</li>
                <li>All daily logs submitted by this user</li>
                <li>All associated data from all tables</li>
              </ul>
              <p className="text-red-600 font-semibold mt-2">
                This action cannot be undone!
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete Permanently
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
});

export default EditUserDialog;
